import { auth } from '@/config/firebase'
import { API_CONFIG } from '@/config/api'
import { VisibilityWatcher } from './visibilityWatcher'
import type { UpdateEvent, UpdateEntityType } from '@/types/api'

/**
 * Filters for a single subscriber. Matches the yaml:1255 query contract:
 *   - entityTypes: comma-joined into `?entityTypes=`
 *   - entityIds:   comma-joined into `?entityIds=`
 *   - since:       ISO-8601 string, forwarded as-is
 *   - once:        closes the stream after the first event (test/debug mode)
 */
export interface UpdatesFilters {
  entityTypes?: UpdateEntityType[]
  entityIds?: string[]
  since?: string
  once?: boolean
}

export type UpdatesHandler = (event: UpdateEvent) => void
export type UpdatesErrorHandler = (error: Event) => void

interface Subscriber {
  filters: UpdatesFilters
  handler: UpdatesHandler
  onError?: UpdatesErrorHandler
}

interface ConnectionEntry {
  /** The live EventSource (recreated on reconnect). */
  source: EventSource | null
  /** Active subscribers for this startupId, fan-out target. */
  subscribers: Set<Subscriber>
  /** Current backoff attempt (0 = first connect). */
  attempt: number
  /** Pending reconnect timer so we can clear it on teardown. */
  reconnectTimer: ReturnType<typeof setTimeout> | null
  /** True once the caller asks for teardown, so in-flight connect aborts. */
  closed: boolean
}

/**
 * Backoff schedule: 1s, 2s, 4s, 8s, 16s, 30s (cap). Includes ±20% jitter per
 * attempt so reconnect storms from N clients don't synchronize. Deterministic
 * in tests via the injected randomFn (defaults to Math.random).
 */
const BACKOFF_CAP_MS = 30_000
const BACKOFF_BASE_MS = 1_000

/**
 * UpdatesService owns the SSE lifecycle for live entity updates.
 *
 * Invariants:
 *   1. At most ONE EventSource per startupId, regardless of subscriber count.
 *   2. The connection is opened on first subscribe and torn down when the
 *      last subscriber unsubscribes. No lingering connections.
 *   3. Subscribers receive events that match their filters; other events are
 *      silently dropped for that subscriber.
 *   4. On `onerror`, the connection reconnects with exponential backoff +
 *      jitter; subscribers stay registered across the reconnect.
 *   5. If a subscriber's filters differ from the existing connection's merged
 *      filters, we reopen the connection with the UNION so the server sends
 *      the superset, and each subscriber filters locally.
 *
 * Native `EventSource` cannot set request headers, so the auth token goes in
 * the `?token=` query param. The backend accepts either Bearer header or
 * query param (see yaml:1255). This matches the legacy hook's approach.
 */
export class UpdatesService {
  /** One entry per startupId, keyed by the path-level `startupId`. */
  private connections = new Map<string, ConnectionEntry>()
  /** Optional seam for tests — replace Math.random with a deterministic fn. */
  private randomFn: () => number = Math.random
  /**
   * Optional seam for tests — override setTimeout/clearTimeout for fake timers.
   * Bound to globalThis on assignment so production calls don't trigger
   * "Illegal invocation" — native setTimeout/clearTimeout in V8 require
   * `this === window`, and storing them as instance properties otherwise
   * binds `this` to the UpdatesService instance.
   */
  private setTimeoutFn: typeof setTimeout = setTimeout.bind(globalThis)
  private clearTimeoutFn: typeof clearTimeout = clearTimeout.bind(globalThis)
  /**
   * True while the tab is hidden. Connections are dropped but subscribers are
   * kept, so resuming re-opens the same set without callers re-subscribing.
   */
  private suspended = false
  private visibility = new VisibilityWatcher()

  /**
   * Inject test seams. Only tests should call this; production uses defaults.
   * Pass `undefined` for any field to leave it at the default.
   */
  _configureForTesting(opts: {
    random?: () => number
    setTimeout?: typeof setTimeout
    clearTimeout?: typeof clearTimeout
  }): void {
    if (opts.random) this.randomFn = opts.random
    if (opts.setTimeout) this.setTimeoutFn = opts.setTimeout
    if (opts.clearTimeout) this.clearTimeoutFn = opts.clearTimeout
  }

  /**
   * Subscribe to updates for a startup. Returns an `unsubscribe` function.
   * Calling unsubscribe twice is safe (second call is a no-op).
   */
  subscribe(
    startupId: string,
    filters: UpdatesFilters,
    handler: UpdatesHandler,
    onError?: UpdatesErrorHandler
  ): () => void {
    if (!startupId) throw new Error('UpdatesService.subscribe: startupId required')

    const subscriber: Subscriber = { filters, handler, onError }
    let entry = this.connections.get(startupId)

    if (!entry) {
      entry = {
        source: null,
        subscribers: new Set([subscriber]),
        attempt: 0,
        reconnectTimer: null,
        closed: false,
      }
      this.connections.set(startupId, entry)
      this.ensureVisibilityListener()
      void this.openConnection(startupId)
    } else {
      // Compute the existing merged filter, then add the new subscriber and
      // recompute. If the union widens (new entityType/entityId, or wildcard
      // promotion), reopen the EventSource so the server streams the
      // superset. Otherwise reuse the live connection — the new subscriber
      // filters locally in dispatch().
      const before = this.mergedFilters(entry)
      entry.subscribers.add(subscriber)
      const after = this.mergedFilters(entry)
      if (this.filtersWidened(before, after)) {
        void this.reopenConnection(startupId)
      }
    }

    let unsubscribed = false
    return () => {
      if (unsubscribed) return
      unsubscribed = true
      this.removeSubscriber(startupId, subscriber)
    }
  }

  /** Tear down all connections. Useful for sign-out / app teardown. */
  closeAll(): void {
    for (const startupId of Array.from(this.connections.keys())) {
      this.teardown(startupId)
    }
    this.removeVisibilityListener()
  }

  private removeSubscriber(startupId: string, subscriber: Subscriber): void {
    const entry = this.connections.get(startupId)
    if (!entry) return
    entry.subscribers.delete(subscriber)
    if (entry.subscribers.size === 0) {
      this.teardown(startupId)
    }
  }

  private teardown(startupId: string): void {
    const entry = this.connections.get(startupId)
    if (!entry) return
    entry.closed = true
    if (entry.reconnectTimer) {
      this.clearTimeoutFn(entry.reconnectTimer)
      entry.reconnectTimer = null
    }
    if (entry.source) {
      entry.source.close()
      entry.source = null
    }
    this.connections.delete(startupId)
    if (this.connections.size === 0) {
      this.removeVisibilityListener()
    }
  }

  /**
   * Suspend on tab hide, resume on tab show.
   *
   * An idle background tab otherwise holds an SSE request open indefinitely,
   * which keeps a server instance alive and billable for as long as the tab
   * exists. Subscribers stay registered across the suspend, so nothing in the
   * UI has to know this happened.
   */
  private ensureVisibilityListener(): void {
    this.suspended = this.visibility.isHidden
    this.visibility.start({
      onHide: () => this.suspendAll(),
      onShow: () => this.resumeAll(),
    })
  }

  private removeVisibilityListener(): void {
    this.visibility.stop()
    this.suspended = false
  }

  /** Drop live sockets, keep subscribers. */
  private suspendAll(): void {
    if (this.suspended) return
    this.suspended = true
    for (const entry of this.connections.values()) {
      if (entry.reconnectTimer) {
        this.clearTimeoutFn(entry.reconnectTimer)
        entry.reconnectTimer = null
      }
      if (entry.source) {
        entry.source.close()
        entry.source = null
      }
      // Don't inherit a long backoff from before the suspend — on resume the
      // user is looking at the page and wants data now.
      entry.attempt = 0
    }
  }

  /** Re-open everything that is still subscribed. */
  private resumeAll(): void {
    if (!this.suspended) return
    this.suspended = false
    for (const startupId of Array.from(this.connections.keys())) {
      const entry = this.connections.get(startupId)
      if (!entry || entry.closed || entry.source) continue
      void this.openConnection(startupId)
    }
  }

  private async getAuthToken(): Promise<string | null> {
    if (!auth?.currentUser) {
      if (import.meta.env.DEV) return 'MOCK_DEV_TOKEN'
      return null
    }
    try {
      return await auth.currentUser.getIdToken()
    } catch (error) {
      console.error('[UpdatesService] Failed to get auth token:', error)
      return null
    }
  }

  private buildUrl(
    startupId: string,
    filters: UpdatesFilters,
    token: string | null
  ): string {
    const base = API_CONFIG.BASE_URL
    const url = new URL(
      `${base}/v1/startups/${encodeURIComponent(startupId)}/updates/stream`
    )
    if (token) url.searchParams.set('token', token)
    if (filters.entityTypes?.length) {
      url.searchParams.set('entityTypes', [...filters.entityTypes].sort().join(','))
    }
    if (filters.entityIds?.length) {
      url.searchParams.set('entityIds', [...filters.entityIds].sort().join(','))
    }
    if (filters.since) url.searchParams.set('since', filters.since)
    if (filters.once) url.searchParams.set('once', 'true')
    return url.toString()
  }

  /**
   * Union of all subscribers' entityTypes/entityIds for the given entry. The
   * server streams the superset; per-subscriber filtering happens on dispatch.
   *
   * `since` and `once` are per-subscriber-semantic and cannot be meaningfully
   * unioned across subscribers (earliest? latest? OR?). They come from the
   * FIRST subscriber only. Reconnect preserves the first subscriber's values
   * even if they've since unsubscribed — server behavior stays consistent
   * across the connection's lifetime.
   */
  private mergedFilters(entry: ConnectionEntry): UpdatesFilters {
    const types = new Set<UpdateEntityType>()
    const ids = new Set<string>()
    let anyTypeWildcard = false
    let anyIdWildcard = false
    for (const sub of entry.subscribers) {
      if (!sub.filters.entityTypes?.length) anyTypeWildcard = true
      else sub.filters.entityTypes.forEach((t) => types.add(t))
      if (!sub.filters.entityIds?.length) anyIdWildcard = true
      else sub.filters.entityIds.forEach((i) => ids.add(i))
    }
    const first = entry.subscribers.values().next().value as Subscriber | undefined
    return {
      entityTypes: anyTypeWildcard ? undefined : Array.from(types),
      entityIds: anyIdWildcard ? undefined : Array.from(ids),
      since: first?.filters.since,
      once: first?.filters.once,
    }
  }

  /**
   * True when `after` is a strict superset of `before` — used to decide
   * whether to reopen a live EventSource on subscriber join. Wildcard
   * (undefined) on either side is treated as "all events": promoting from
   * narrow to wildcard widens; demoting wildcard back never happens until
   * teardown.
   */
  private filtersWidened(
    before: UpdatesFilters,
    after: UpdatesFilters
  ): boolean {
    const widened = (
      a: UpdateEntityType[] | string[] | undefined,
      b: UpdateEntityType[] | string[] | undefined
    ): boolean => {
      // Already wildcard before → can't widen further by union semantics.
      if (a === undefined) return false
      // Going from narrow to wildcard always widens.
      if (b === undefined) return true
      const aSet = new Set(a as string[])
      // Adding subscribers only ever unions, so any element in b but not
      // in a means the merged filter widened.
      return (b as string[]).some((x) => !aSet.has(x))
    }
    return (
      widened(before.entityTypes, after.entityTypes) ||
      widened(before.entityIds, after.entityIds)
    )
  }

  /** Close the current source and re-open with the latest merged filters. */
  private async reopenConnection(startupId: string): Promise<void> {
    const entry = this.connections.get(startupId)
    if (!entry || entry.closed) return
    // Pre-open phase: the original openConnection() is still awaiting its
    // token fetch. mergedFilters is read at that point, so the in-flight
    // open will already use the widened union. Spawning another open would
    // race and produce two EventSource instances.
    if (!entry.source) return
    if (entry.reconnectTimer) {
      this.clearTimeoutFn(entry.reconnectTimer)
      entry.reconnectTimer = null
    }
    entry.source.close()
    entry.source = null
    // Reset attempt — this is a deliberate filter-driven reconnect, not a
    // failure-recovery path.
    entry.attempt = 0
    await this.openConnection(startupId)
  }

  private async openConnection(startupId: string): Promise<void> {
    const entry = this.connections.get(startupId)
    if (!entry || entry.closed) return

    const token = await this.getAuthToken()
    // Re-check after the async gap — teardown may have run while the token
    // fetch was in flight (removeSubscriber deletes the entry when the last
    // subscriber leaves, so the `closed` flag plus the map check cover both
    // the "still draining" and the "never-had-subscribers" states).
    const latest = this.connections.get(startupId)
    if (!latest || latest.closed) return
    // The tab went hidden during the token fetch — resumeAll() will reopen.
    if (this.suspended) return

    // Defer opening when we have no token in production. Opening an SSE
    // with no auth would 401 → onerror → reconnect storm, so back off and
    // let the auth bootstrap settle. DEV has its MOCK_DEV_TOKEN fallback
    // and is allowed through.
    if (!token && !import.meta.env.DEV) {
      this.scheduleReconnect(startupId)
      return
    }

    const url = this.buildUrl(startupId, this.mergedFilters(latest), token)
    const source = new EventSource(url)
    latest.source = source

    source.addEventListener('update', (event) => {
      try {
        const data = JSON.parse((event as MessageEvent).data) as UpdateEvent
        this.dispatch(startupId, data)
      } catch (error) {
        console.error('[UpdatesService] Failed to parse update event:', error)
      }
    })

    // `event: ping` keepalives are intentionally unhandled (no-op). The
    // browser's EventSource keeps the connection alive on any message type.

    source.onopen = () => {
      const e = this.connections.get(startupId)
      if (e) e.attempt = 0
    }

    source.onerror = (event) => {
      const e = this.connections.get(startupId)
      if (!e || e.closed) return
      // Notify subscribers first so UI can surface the error.
      for (const sub of e.subscribers) sub.onError?.(event)
      // Then tear down and schedule a reconnect.
      if (e.source) {
        e.source.close()
        e.source = null
      }
      this.scheduleReconnect(startupId)
    }
  }

  private dispatch(startupId: string, event: UpdateEvent): void {
    const entry = this.connections.get(startupId)
    if (!entry) return
    for (const sub of entry.subscribers) {
      if (!this.matchesFilters(event, sub.filters)) continue
      try {
        sub.handler(event)
      } catch (error) {
        // One bad subscriber must not take down the dispatch loop.
        console.error('[UpdatesService] subscriber handler threw:', error)
      }
    }
  }

  private matchesFilters(event: UpdateEvent, filters: UpdatesFilters): boolean {
    if (filters.entityTypes?.length && !filters.entityTypes.includes(event.entityType)) {
      return false
    }
    if (filters.entityIds?.length && !filters.entityIds.includes(event.entityId)) {
      return false
    }
    return true
  }

  private scheduleReconnect(startupId: string): void {
    const entry = this.connections.get(startupId)
    if (!entry || entry.closed) return
    if (this.suspended) return
    const delay = this.computeBackoffDelay(entry.attempt)
    entry.attempt += 1
    entry.reconnectTimer = this.setTimeoutFn(() => {
      const e = this.connections.get(startupId)
      if (!e || e.closed) return
      e.reconnectTimer = null
      void this.openConnection(startupId)
    }, delay)
  }

  /** 1s, 2s, 4s, 8s, 16s, 30s cap — with ±20% jitter applied per attempt. */
  private computeBackoffDelay(attempt: number): number {
    const raw = Math.min(BACKOFF_BASE_MS * 2 ** attempt, BACKOFF_CAP_MS)
    const jitter = (this.randomFn() * 0.4 - 0.2) * raw
    return Math.max(0, Math.floor(raw + jitter))
  }
}

export const updatesService = new UpdatesService()
